/**
 * Выключатель «Автоодобрение» в шапке — docs/ai-lab.md, решение Ильи 2026-10-25.
 *
 * ЧТО ОН ВКЛЮЧАЕТ. Обдуманное исключение из FR-4.1: включённое автоодобрение одобряет КАЖДУЮ
 * НОВУЮ ВЕРСИЮ писателя бренда, ставшую «ожидает», — и новый пост, и переделку по замечанию, —
 * в ближайший свободный слот без человека, и пост уходит в канал текущего режима бренда обычным
 * путём публикации. Версию, которую человек уже отложил или одобрил, автомат не берёт.
 * Исключение держат три замка (конфиг бренда и этот выключатель, allowlist демона подписи,
 * allowlist публикатора); выключатель — только один из них, и включённый он не обещает, что
 * посты пойдут: остальные замки могут не пустить. У бренда без писателя (`generationOff`) новых
 * версий нет вовсе — диалог включения говорит это словами.
 *
 * ЗА ЧТО ВКЛЮЧИВШИЙ ОТВЕЧАЕТ. Решения и ключи автомата пишутся на того, кто включил, и только в
 * канал, который назвал диалог включения: смена режима бренда автоодобрение выключает, как и
 * отключение учётной записи включившего (docs/ai-lab.md §3.5).
 *
 * КОГДА ЕГО ВИДНО. Только у бренда, чей конфиг автоодобрение разрешает
 * (`auto_approval.allowed` в `GET /api/v1/brands`, `autoApprovalAllowed` в `brand.tsx`); у
 * остальных брендов органа нет вовсе — шапка ровно прежняя. Переключает администратор (на
 * бэкенде — тот же замок, что у режима публикаций); остальные видят состояние и подсказку.
 *
 * ФОРМА — ПИЛЮЛЯ, А НЕ ВТОРОЙ ТУМБЛЕР. Рядом стоит тумблер режима публикаций, и два одинаковых
 * тумблера читались бы одним органом в двух экземплярах. Включённое — пилюля акцентной
 * подложки с акцентной рамкой и точкой: заметна боковым зрением, но не заливка — заливка в
 * шапке одна, у главной кнопки. Выключенное — серый контур со словом «выкл».
 *
 * ПОДТВЕРЖДЕНИЕ ОБЯЗАТЕЛЬНО В ОБЕ СТОРОНЫ, и оба называют последствие:
 *   • включение — что версии пойдут БЕЗ человека, в какой канал (режим и id), на чьё имя, что
 *     уже ждущие решения версии не тронуты, а переделка, пришедшая после включения, — тронута.
 *     Пока режим или канал не известны или канал режима не задан, включить нельзя: человек
 *     обязан видеть, куда уйдут посты, до нажатия, а бэкенд «в никуда» не включает;
 *   • выключение — что автоодобренные посты, ждущие слота, вернутся на согласование, а уже
 *     взятые публикатором — нет; после ответа диалог показывает оба числа из ответа.
 * Тело несёт `expected_state` — положение, которое видел человек: переключение из соседней
 * вкладки между показом и нажатием даёт 409, а не «включил то, что уже выключили».
 *
 * СОСТОЯНИЕ ГРУЗИТ ШАПКА (`Shell.tsx`), как режим: орган получает сырой ответ и разбирает его
 * схемой. Чужой ответ — серый орган без утверждения о положении: «включено» по неразобранному
 * ответу было бы враньём в самом дорогом месте шапки.
 */

import {
  AUTO_APPROVAL_NOT_ALLOWED_REASON,
  type PublicationDispatchState,
} from '@smm/contracts/schemas';
import { type JSX, useCallback, useId, useState } from 'react';
import { ApiRequestError, post } from '../api/client.ts';
import {
  type AutoApproval,
  AutoApprovalSchema,
  type SetAutoApprovalBody,
  type SetAutoApprovalResponse,
  SetAutoApprovalResponseSchema,
} from '../api/types.ts';
import {
  CHANNEL_BRAND_UNKNOWN_TEXT,
  CHANNEL_LOADING_TEXT,
  CHANNEL_UNKNOWN_TEXT,
  readyChatId,
  useModeChannel,
} from '../api/useModeChannel.ts';
import styles from './AutoApprovalSwitch.module.css';
import { plural, shortMsk } from './format.ts';
import { Modal } from './Modal.tsx';

export interface AutoApprovalSwitchProps {
  /** Чей выключатель: id бренда из адреса — уходит в тело переключения. */
  readonly brand: string;
  /** Название бренда для подсказки и подтверждения. */
  readonly brandTitle: string;
  /** Сырой ответ `GET /api/v1/auto-approval?brand=`; `undefined` — ещё едет или не пришёл. */
  readonly data: unknown;
  readonly failed: boolean;
  /** Переключает только администратор — у остальных это индикатор. */
  readonly canManage: boolean;
  /** Режим публикаций бренда — им подтверждение включения называет канал. */
  readonly mode: PublicationDispatchState | undefined;
  /**
   * Причина, по которой у бренда нет писателя (`agentsOffReason(…, ['generation'])`), или
   * `null`. Автоодобрять бренду без писателя нечего — включить можно (владелец вправе включить
   * заранее), но диалог и подсказка говорят это словами.
   */
  readonly generationOff?: string | null;
  /** Логин того, кто смотрит: включение пишет решения и ключи на него. */
  readonly viewerLogin?: string | null;
  readonly onChanged: () => void;
}

/** Ответ, разобранный схемой и принадлежащий ЭТОМУ бренду; иначе `null`. */
export function autoApprovalOf(data: unknown, brand: string): AutoApproval | null {
  const r = AutoApprovalSchema.safeParse(data);
  return r.success && r.data.brand === brand ? r.data : null;
}

export function AutoApprovalSwitch({
  brand,
  brandTitle,
  data,
  failed,
  canManage,
  mode,
  generationOff = null,
  viewerLogin = null,
  onChanged,
}: AutoApprovalSwitchProps): JSX.Element | null {
  const tipId = useId();
  const [confirming, setConfirming] = useState<'on' | 'off' | null>(null);
  const state = data === undefined ? null : autoApprovalOf(data, brand);
  // Живёт всю жизнь органа: `Modal` держит фокус эффектом от `onClose`, а шапка
  // перерисовывается после переключения — новая функция увела бы фокус с диалога итога.
  const close = useCallback((): void => {
    setConfirming(null);
  }, []);

  // Диалог рисуется ВНЕ веток состояния: перечитывание после переключения не должно
  // закрывать его посреди показа итога выключения.
  const dialog =
    confirming === null ? null : (
      <AutoApprovalConfirm
        brand={brand}
        brandTitle={brandTitle}
        target={confirming}
        mode={mode}
        generationOff={generationOff}
        viewerLogin={viewerLogin}
        onClose={close}
        onChanged={onChanged}
      />
    );

  if (state === null) {
    // Едет и не провалилось — молчим, как режим: мигание «выкл» — тоже утверждение.
    if (data === undefined && !failed) return dialog;
    return (
      <div className={styles['wrap']}>
        <span
          className={`${styles['sw']} ${styles['unknown']}`}
          title="Автоодобрение: состояние неизвестно"
        >
          <span className={styles['dot']} aria-hidden="true" />
          <span className={styles['long']}>Автоодобрение</span>
          <span className={styles['short']}>Авто</span>
          <span className={styles['state']}>—</span>
        </span>
        <span className={styles['tip']} role="tooltip">
          <b>Автоодобрение · состояние неизвестно</b>
          <span>{`Состояние автоодобрения ${brandTitle} не загрузилось. Обновите страницу.`}</span>
        </span>
        {dialog}
      </div>
    );
  }

  const on = state.state === 'on';
  const word = on ? 'включено' : 'выключено';

  return (
    <div className={styles['wrap']}>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-describedby={tipId}
        aria-label={`Автоодобрение ${brandTitle}: ${word}`}
        className={`${styles['sw']} ${on ? styles['on'] : styles['off']}`}
        disabled={!canManage}
        onClick={() => {
          setConfirming(on ? 'off' : 'on');
        }}
      >
        <span className={styles['dot']} aria-hidden="true" />
        <span className={styles['long']}>Автоодобрение</span>
        <span className={styles['short']}>Авто</span>
        <span className={styles['state']}>{on ? 'вкл' : 'выкл'}</span>
      </button>

      <span className={styles['tip']} role="tooltip" id={tipId}>
        <b>{`Автоодобрение ${word} · ${brandTitle}`}</b>
        <span>
          {on
            ? 'Каждая новая версия писателя, ставшая «ожидает», — в том числе переделка — одобряется автоматически, без решения человека, в ближайший свободный слот и уходит в канал, подтверждённый при включении.'
            : 'Посты ждут решения человека. Включённое, автоодобрение одобряло бы новые версии писателя без человека.'}
        </span>
        {generationOff === null ? null : (
          <span className={styles['tipNote']}>
            {`У бренда нет писателя (${generationOff}): автоодобрять нечего.`}
          </span>
        )}
        <span className={styles['tipNote']}>
          Есть только у брендов, которым его разрешает конфиг; у других брендов его нет.
        </span>
        {state.since === null ? (
          <span className={styles['tipNote']}>Не переключалось.</span>
        ) : state.by === null ? (
          // Момент есть, человека нет: положение поставил сам бэкенд — на старте он выключает
          // строку бренда, чей конфиг автоодобрение больше не разрешает.
          <span className={styles['tipNote']}>{`вне панели · ${shortMsk(state.since)}`}</span>
        ) : (
          <span className={styles['tipNote']}>
            {`${state.by.login} · ${shortMsk(state.since)}`}
          </span>
        )}
        {canManage ? null : <span className={styles['tipNote']}>Переключает администратор.</span>}
      </span>
      {dialog}
    </div>
  );
}

/** Отказ переключения — словами; `refresh` — состояние на сервере уже другое, перечитать. */
export function autoApprovalErrorText(e: unknown): {
  readonly text: string;
  readonly refresh: boolean;
} {
  if (!(e instanceof ApiRequestError)) {
    return { text: e instanceof Error ? e.message : String(e), refresh: false };
  }
  const d = e.details;
  const reason =
    typeof d === 'object' && d !== null ? (d as Record<string, unknown>)['reason'] : undefined;
  if (reason === AUTO_APPROVAL_NOT_ALLOWED_REASON) {
    return {
      text: 'Конфиг бренда не разрешает автоодобрение — включить его нельзя. Обновите страницу.',
      refresh: true,
    };
  }
  if (reason === 'channel_not_configured') {
    // Канал режима пуст в конфиге: включать автоодобрение «в никуда» бэкенд не даёт.
    return {
      text: `${e.message}. Автоодобрение не включено: сначала канал режима вписывают в конфиг бренда.`,
      refresh: false,
    };
  }
  if (e.status === 409) {
    return {
      text: `${e.message}. Положение успели изменить — в другой вкладке или другим администратором; состояние перечитано, проверьте его и повторите.`,
      refresh: true,
    };
  }
  if (e.status === 403) {
    return { text: 'Переключает только администратор.', refresh: false };
  }
  return { text: e.message, refresh: false };
}

/**
 * Подтверждение. Включение называет канал, куда уйдут посты без человека; выключение после
 * ответа показывает, что оно сделало, — числами из ответа, а не обещанием.
 */
function AutoApprovalConfirm({
  brand,
  brandTitle,
  target,
  mode,
  generationOff,
  viewerLogin,
  onClose,
  onChanged,
}: {
  brand: string;
  brandTitle: string;
  target: 'on' | 'off';
  mode: PublicationDispatchState | undefined;
  generationOff: string | null;
  viewerLogin: string | null;
  onClose: () => void;
  onChanged: () => void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<SetAutoApprovalResponse | 'unparsed' | null>(null);
  const { channel } = useModeChannel(brand);
  const toOn = target === 'on';

  // Куда уйдут посты — словами и id. Неизвестно — включать нельзя (шапка файла).
  const chatId = readyChatId(channel);
  const modeWord = mode === 'live' ? 'боевой' : mode === 'test' ? 'тестовый' : null;
  const channelUnknown =
    channel.kind === 'unknown'
      ? CHANNEL_BRAND_UNKNOWN_TEXT
      : channel.kind === 'failed' || channel.kind === 'malformed'
        ? CHANNEL_UNKNOWN_TEXT
        : null;
  // Канал ещё едет — не ошибка, а ожидание: нейтральная строка, как в диалоге одобрения, а не
  // красная тревога, мигающая при каждом открытии. Включение при этом закрыто.
  const channelLoading = channel.kind === 'loading';
  // Канал режима не задан в конфиге — бэкенд не включит (`channel_not_configured`): закрыто.
  const channelMissing = channel.kind === 'missing';
  const blockedOn =
    toOn && (modeWord === null || channelUnknown !== null || channelLoading || channelMissing);

  const submit = (): void => {
    if (busy || blockedOn) return;
    setBusy(true);
    setError(null);
    const body: SetAutoApprovalBody = {
      brand,
      state: target,
      expected_state: toOn ? 'off' : 'on',
    };
    post<unknown>('/api/v1/auto-approval', body)
      .then((raw) => {
        setBusy(false);
        onChanged();
        if (toOn) {
          onClose();
          return;
        }
        // Выключение: диалог остаётся и показывает, что выключение сделало.
        const r = SetAutoApprovalResponseSchema.safeParse(raw);
        setDone(r.success ? r.data : 'unparsed');
      })
      .catch((e: unknown) => {
        setBusy(false);
        const f = autoApprovalErrorText(e);
        setError(f.text);
        if (f.refresh) onChanged();
      });
  };

  if (done !== null) {
    return (
      <Modal title={`Автоодобрение ${brandTitle} выключено`} width={460} onClose={onClose}>
        {done === 'unparsed' ? (
          <div className="val val-warn" role="status">
            <span>
              Выключено, но ответ сервера не разобрался — сколько постов вернулось на согласование,
              отсюда не видно. Проверьте бэклог.
            </span>
          </div>
        ) : (
          <>
            <div className="val val-info" role="status">
              <span>
                {done.returned_to_review === 0
                  ? 'Ни одного автоодобренного поста в слотах не было — возвращать на согласование нечего.'
                  : `Вернулось на согласование: ${String(done.returned_to_review)} ${plural(done.returned_to_review, 'пост', 'поста', 'постов')} — ключи отозваны, слоты свободны.`}
              </span>
            </div>
            {done.left_in_flight === 0 ? null : (
              <div className="val val-warn" role="alert">
                <span>
                  {`Уже у публикатора: ${String(done.left_in_flight)} ${plural(done.left_in_flight, 'пост', 'поста', 'постов')} — выключение их не остановило, исход решит публикатор.`}
                </span>
              </div>
            )}
          </>
        )}
        <div className={styles['actions']}>
          <button type="button" className="btn btn-pri btn-sm" onClick={onClose}>
            Готово
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={`${toOn ? 'Включить' : 'Выключить'} автоодобрение ${brandTitle}?`}
      width={460}
      onClose={onClose}
    >
      {toOn ? (
        <div className="val val-warn">
          <span>
            {`Каждая новая версия писателя ${brandTitle}, ставшая «ожидает», — и новый пост, и переделка по замечанию — будет одобрена и опубликована БЕЗ решения человека — в ближайший свободный слот`}
            {modeWord === null
              ? ' и в канал текущего режима.'
              : `, в ${modeWord} канал${chatId === null ? '' : ` ${chatId}`} — канал текущего режима бренда.`}
            {
              ' Версии, которые ждали решения до включения, автоодобрение не тронет; но переделку такого поста, пришедшую после включения, одобрит.'
            }
            {viewerLogin === null ? '' : ` Решения и ключи будут записаны на вас (${viewerLogin}).`}
            {' Смена режима публикаций бренда выключит автоодобрение. Другие бренды не меняются.'}
          </span>
        </div>
      ) : (
        <div className="val val-info">
          <span>
            Новые посты писателя снова будут ждать решения человека. Автоодобренные посты, которые
            ещё ждут своего слота, вернутся на согласование: ключ отзовётся, слот освободится.
            Посты, которые публикатор уже взял, выключение не остановит — сколько их, диалог покажет
            после выключения.
          </span>
        </div>
      )}

      {toOn && modeWord === null ? (
        <div className="val val-err" role="alert">
          <span>
            Режим публикаций не загрузился — неизвестно, в какой канал пойдут посты. Обновите
            страницу.
          </span>
        </div>
      ) : null}
      {toOn && channelUnknown !== null ? (
        <div className="val val-err" role="alert">
          <span>{channelUnknown}</span>
        </div>
      ) : null}
      {toOn && channelLoading ? <div className="micro">{CHANNEL_LOADING_TEXT}</div> : null}
      {toOn && channelMissing ? (
        <div className="val val-err" role="alert">
          <span>
            Канал текущего режима в конфиге бренда не задан — автоодобрению некуда публиковать.
            Включить можно после того, как канал впишут.
          </span>
        </div>
      ) : null}
      {toOn && generationOff !== null ? (
        <div className="val val-warn">
          <span>
            {`У бренда нет писателя (${generationOff}): новых версий не будет, и автоодобрять нечего, пока генерацию не подключат.`}
          </span>
        </div>
      ) : null}

      {error === null ? null : (
        <div className="val val-err" role="alert">
          <span>{error}</span>
        </div>
      )}

      <div className={styles['actions']}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
          Отмена
        </button>
        <button
          type="button"
          className={busy || blockedOn ? 'btn btn-sm btn-dis' : 'btn btn-pri btn-sm'}
          disabled={busy || blockedOn}
          onClick={submit}
        >
          {toOn ? 'Включить автоодобрение' : 'Выключить'}
        </button>
      </div>
    </Modal>
  );
}
